import React from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { SEOHead } from '../components/SEOHead';
import { Hero } from '../components/Hero';
import { Highlights } from '../components/Highlights';
import ProductShowcase from '../components/ProductShowcase';
import { WholesaleCta } from '../components/WholesaleCta';

const SITE = process.env.REACT_APP_SITE_URL || 'https://cajeta-hacienda-real.com';

const homeSchema = (lang, region) => [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${SITE}/${region}/#webpage`,
    'url': `${SITE}/${region}`,
    'name': lang === 'es'
      ? 'Cajeta Natural de Leche de Vaca | Hacienda Real'
      : 'Natural Cow\'s Milk Cajeta | Hacienda Real',
    'description': lang === 'es'
      ? 'Cajeta y dulces 100% naturales elaborados con leche entera de vaca, sin conservadores. Tradición artesanal desde 1950 en San Juan de los Lagos, Jalisco.'
      : '100% natural cajeta and sweets made from whole cow\'s milk, no preservatives. Artisan tradition since 1950 in San Juan de los Lagos, Jalisco.',
    'isPartOf': { '@id': `${SITE}/#website` },
    'about': { '@id': `${SITE}/#organization` },
    'inLanguage': lang === 'es' ? 'es-MX' : 'en-US',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': lang === 'es'
      ? [
          {
            '@type': 'Question',
            'name': '¿Con qué leche está hecha la cajeta Hacienda Real?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Nuestra cajeta se elabora exclusivamente con leche entera de vaca de la más alta calidad, sin leche en polvo ni sustitutos. Esto le da su sabor auténtico y textura cremosa.',
            },
          },
          {
            '@type': 'Question',
            'name': '¿La cajeta Hacienda Real lleva conservadores?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'No. Nuestra cajeta es 100% natural, sin conservadores, colorantes ni saborizantes artificiales. Solo leche de vaca, azúcar y la receta original desde 1950.',
            },
          },
          {
            '@type': 'Question',
            'name': '¿Dónde puedo comprar cajeta Hacienda Real?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Puedes comprar cajeta Hacienda Real en Walmart México, Bodega Aurrerá, Mercado Libre y TikTok Shop. También disponible con envíos a toda la República Mexicana y Estados Unidos.',
            },
          },
          {
            '@type': 'Question',
            'name': '¿Cuánto dura la cajeta Hacienda Real?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Al ser un producto natural sin conservadores, la cajeta Hacienda Real tiene una vida útil indicada en el frasco. Se recomienda refrigerar una vez abierta.',
            },
          },
        ]
      : [
          {
            '@type': 'Question',
            'name': 'What milk is Hacienda Real cajeta made from?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Our cajeta is made exclusively from whole cow\'s milk of the highest quality — no powdered milk or substitutes. This gives it its authentic flavor and creamy texture.',
            },
          },
          {
            '@type': 'Question',
            'name': 'Does Hacienda Real cajeta have preservatives?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'No. Our cajeta is 100% natural with no preservatives, artificial colors, or flavors. Just cow\'s milk, sugar, and our original recipe from 1950.',
            },
          },
          {
            '@type': 'Question',
            'name': 'Where can I buy Hacienda Real cajeta?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'You can buy Hacienda Real cajeta on TikTok Shop and Mercado Libre, with shipping to Mexico and the United States.',
            },
          },
        ],
  },
];

export default function HomePage() {
  const { region = 'mx' } = useParams();
  const { lang } = useLanguage();

  const isMx = region === 'mx';
  const title = lang === 'es'
    ? 'Cajeta Natural de Leche de Vaca – Dulces Artesanales desde 1950'
    : 'Natural Cow\'s Milk Cajeta – Artisan Sweets since 1950';
  const description = lang === 'es'
    ? 'Cajeta y dulces de leche 100% naturales elaborados con leche entera de vaca, sin conservadores ni aditivos. Tradición artesanal desde 1950 en San Juan de los Lagos, Jalisco. Envíos a México y EE. UU.'
    : 'Natural cow\'s milk cajeta and dairy sweets — no preservatives, no additives. Artisan tradition since 1950 in San Juan de los Lagos, Jalisco, Mexico. Shipping to Mexico and the USA.';

  return (
    <>
      <SEOHead
        title={title}
        description={description}
        canonical={`/${region}`}
        lang={lang}
        altLang={{ es: '/mx', en: '/us' }}
        schema={homeSchema(lang, region)}
      />
      <Hero />
      <Highlights />
      <ProductShowcase />
      <WholesaleCta />
    </>
  );
}
